import {
    ArrayExpr,
    BinaryExpr,
    CellReference,
    ComparisonExpr,
    ConcatExpr,
    FormulaValueExpression,
    FrameCalculatedColumnExpr,
    FrameColumnReference,
    IdentifierLiteral,
    IntersectionReference,
    InvokeFunctionExpr,
    LazyFormulaExpression,
    ListReference,
    LiveFormulaExpression,
    NegateExpr,
    ParenExpr,
    PercentExpr,
    RangeReference,
    UnionReference,
    ValueLiteral
} from '@mintdata-wsp/common/controllers/spreadsheet/formula/ast/FormulaASTNodes';
import {BinaryOpType, CompareOpType, IFormulaNode} from '@mintdata-wsp/common/controllers/spreadsheet/formula/ast/FormulaDefinitions';
import {BaseFormulaOps} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/BaseFormulaOps';
import {OhmNode} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/OhmEngine';
import {MeshLocationUtils} from '@mintdata-wsp/common/util/MeshLocationUtils';
import {area, mk} from '@mintdata/mint-kit/api/log/mk';
import {SheetId, WorkbookId} from '@mintdata/mint-kit/api/models/Identifiers';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {AInteger} from '@mintdata/mint-kit/api/values/AInteger';
import {ANumber} from '@mintdata/mint-kit/api/values/ANumber';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {EvalErrors} from '@mintdata/mint-kit/api/values/errors/EvalErrors';

export interface IOhmParseContext
{
} // marker interface

export interface IFormulaParseContext extends IOhmParseContext
{
    readonly workbookId:WorkbookId;
    readonly sheetId:SheetId;
    readonly colIndex:number;
    readonly rowIndex:number;

    findSheetIdByName(workbookId:WorkbookId, sheetName:string):SheetId;
}

export class BuildFormulaAST extends BaseFormulaOps<IFormulaParseContext>
{
    public constructor()
    {
        super('eval');
    }

    FormulaValueA1(expr:OhmNode):IFormulaNode
    {
        const contents = this.__contents(expr);
        mk.debug(area.Formula, `[eval] FormulaValueA1: ${contents}`);

        return expr.eval();
    }

    FormulaValueRC(expr:OhmNode):IFormulaNode
    {
        const contents = this.__contents(expr);
        mk.debug(area.Formula, `[eval] FormulaValueRC: ${contents}`);

        return expr.eval();
    }

    FormulaValue(expr:OhmNode):IFormulaNode
    {
        const contents = this.__contents(expr);
        mk.debug(area.Formula, `[eval] FormulaValue: ${contents}`);

        return expr.eval();
    }

    FormulaValue_magic(expr:OhmNode):IFormulaNode
    {
        const contents = (expr.source as any).sourceString;
        mk.debug(area.Formula, `[eval] FormulaValue_magic: ${contents}`);

        let value:IValue = AString.fromString(contents);

        if( contents.match(/^\s*([\-\+]?)\s*(\d+)\s*$/) )
        {
            const trimmedContents = contents.replace(/^\s*([\-\+]?)\s*(\d+)\s*$/, '$1$2');
            const integerValue:AInteger = AInteger.fromNumber(parseInt(trimmedContents, 10));

            if( integerValue.toString() === contents )
                value = integerValue;
        }
        else if( contents.match(/^\s*([\-\+]?)\s*((\d+\.\d*)|(\.\d+))\s*$/) )
        {
            const trimmedContents = contents.replace(/^\s*([\-\+]?)\s*((\d+\.\d*)|(\.\d+))\s*$/, '$1$2');
            value = ANumber.fromNumber(parseFloat(trimmedContents));
        }
        else if( contents.match(/^\s*true\s*$/i) )
            value = ABoolean.true();
        else if( contents.match(/^\s*false\s*$/i) )
            value = ABoolean.false();

        const result = new ValueLiteral(value);

        mk.debug(area.Formula, `[eval] FormulaValue_magic: ${contents} => ${result}`);
        return result;
    }

    FormulaValue_simple(formulaTextSymbol:OhmNode, expr:OhmNode):IFormulaNode
    {
        const contents = (expr.source as any).sourceString.slice(formulaTextSymbol.source.endIdx);
        mk.debug(area.Formula, `[eval] FormulaValue_simple: ${contents}`);

        const value = AString.fromString(contents);

        const result = new ValueLiteral(value);

        mk.debug(area.Formula, `[eval] FormulaValue_simple: ${contents} => ${result}`);
        return result;
    }

    FormulaValue_expression(formulaStartSymbol:OhmNode, expr:OhmNode):IFormulaNode
    {
        const startSymbol = this.__contents(formulaStartSymbol);

        const contents = this.__contents(expr);
        mk.debug(area.Formula, `[eval] FormulaValue_expression: ${contents}`);

        return new FormulaValueExpression(expr.eval(), startSymbol.indexOf('+=') >= 0);
    }

    FormulaValue_lazyExpression(formulaStartSymbol:OhmNode, expr:OhmNode):IFormulaNode
    {
        const startSymbol = this.__contents(formulaStartSymbol);

        const contents = this.__contents(expr);
        mk.debug(area.Formula, `[eval] FormulaValue_lazyExpression: ${contents}`);

        return new LazyFormulaExpression(expr.eval(), startSymbol.indexOf('+=') >= 0);
    }

    FormulaValue_liveExpression(formulaStartSymbol:OhmNode, expr:OhmNode):IFormulaNode
    {
        const startSymbol = this.__contents(formulaStartSymbol);

        const contents = this.__contents(expr);
        mk.debug(area.Formula, `[eval] FormulaValue_liveExpression: ${contents}`);

        return new LiveFormulaExpression(expr.eval(), startSymbol.indexOf('+=') >= 0);
    }

    Expression(anyExpr:OhmNode):IFormulaNode
    {
        const contents = this.__contents(anyExpr);
        mk.debug(area.Formula, `[eval] Expression: ${contents}`);

        return anyExpr.eval();
    }

    ComparisonExpression_lt(lhs:OhmNode, op:OhmNode, rhs:OhmNode):IFormulaNode
    {
        const contents = this.__contents(lhs, op, rhs);
        mk.debug(area.Formula, `[eval] ComparisonExpression_lt: ${contents}`);

        return this.__evalComparisonExpression(lhs, rhs, CompareOpType.Lt);
    }

    ComparisonExpression_gt(lhs:OhmNode, op:OhmNode, rhs:OhmNode):IFormulaNode
    {
        const contents = this.__contents(lhs, op, rhs);
        mk.debug(area.Formula, `[eval] ComparisonExpression_gt: ${contents}`);

        return this.__evalComparisonExpression(lhs, rhs, CompareOpType.Gt);
    }

    ComparisonExpression_equal(lhs:OhmNode, op:OhmNode, rhs:OhmNode):IFormulaNode
    {
        const contents = this.__contents(lhs, op, rhs);
        mk.debug(area.Formula, `[eval] ComparisonExpression_equal: ${contents}`);

        return this.__evalComparisonExpression(lhs, rhs, CompareOpType.Equal);
    }

    ComparisonExpression_notEqual(lhs:OhmNode, op:OhmNode, rhs:OhmNode):IFormulaNode
    {
        const contents = this.__contents(lhs, op, rhs);
        mk.debug(area.Formula, `[eval] ComparisonExpression_notEqual: ${contents}`);

        return this.__evalComparisonExpression(lhs, rhs, CompareOpType.NotEqual);
    }

    ComparisonExpression_le(lhs:OhmNode, op:OhmNode, rhs:OhmNode):IFormulaNode
    {
        const contents = this.__contents(lhs, op, rhs);
        mk.debug(area.Formula, `[eval] ComparisonExpression_le: ${contents}`);

        return this.__evalComparisonExpression(lhs, rhs, CompareOpType.Le);
    }

    ComparisonExpression_ge(lhs:OhmNode, op:OhmNode, rhs:OhmNode):IFormulaNode
    {
        const contents = this.__contents(lhs, op, rhs);
        mk.debug(area.Formula, `[eval] ComparisonExpression_ge: ${contents}`);

        return this.__evalComparisonExpression(lhs, rhs, CompareOpType.Ge);
    }

    ConcatExpression_con(lhs:OhmNode, op:OhmNode, rhs:OhmNode):IFormulaNode
    {
        const contents = this.__contents(lhs, op, rhs);
        mk.debug(area.Formula, `[eval] ConcatExpression_con: ${contents}`);

        const lhsContents:string = this.__contents(lhs);
        const rhsContents:string = this.__contents(rhs);

        const result = new ConcatExpr(lhs.eval(), rhs.eval());

        mk.debug(area.Formula, `[eval] ConcatExpression_con(): ${lhsContents}, ${rhsContents} => ${result}`);
        return result;
    }

    AddExpression_add(lhs:OhmNode, op:OhmNode, rhs:OhmNode):IFormulaNode
    {
        const contents = this.__contents(lhs, op, rhs);
        mk.debug(area.Formula, `[eval] AddExpression_add: ${contents}`);

        return this.__evalBinaryOperands(lhs, rhs, BinaryOpType.Add);
    }

    AddExpression_sub(lhs:OhmNode, op:OhmNode, rhs:OhmNode):IFormulaNode
    {
        const contents = this.__contents(lhs, op, rhs);
        mk.debug(area.Formula, `[eval] AddExpression_sub: ${contents}`);

        return this.__evalBinaryOperands(lhs, rhs, BinaryOpType.Subtract);
    }

    MultiExpression_mul(lhs:OhmNode, op:OhmNode, rhs:OhmNode):IFormulaNode
    {
        const contents = this.__contents(lhs, op, rhs);
        mk.debug(area.Formula, `[eval] MultiExpression_mul: ${contents}`);

        return this.__evalBinaryOperands(lhs, rhs, BinaryOpType.Multiply);
    }

    MultiExpression_div(lhs:OhmNode, op:OhmNode, rhs:OhmNode):IFormulaNode
    {
        const contents = this.__contents(lhs, op, rhs);
        mk.debug(area.Formula, `[eval] MultiExpression_div: ${contents}`);

        return this.__evalBinaryOperands(lhs, rhs, BinaryOpType.Divide);
    }

    ExponentiationExpression_pow(lhs:OhmNode, op:OhmNode, rhs:OhmNode):IFormulaNode
    {
        const contents = this.__contents(lhs, op, rhs);
        mk.debug(area.Formula, `[eval] ExponentiationExpression_pow: ${contents}`);

        return this.__evalBinaryOperands(lhs, rhs, BinaryOpType.Power);
    }

    PercentExpression_percent(expr:OhmNode, precentSign:OhmNode):IFormulaNode
    {
        const contents:string = this.__contents(expr, precentSign);
        mk.debug(area.Formula, `[eval] PercentExpression_percent: ${contents}`);

        const exprValue:IFormulaNode = expr.eval();
        const result:IFormulaNode = new PercentExpr(exprValue);

        mk.debug(area.Formula, `[eval] PercentExpression_percent: ${contents} => ${result}`);
        return result;
    }

    UnaryExpression_negate(minusSign:OhmNode, expr:OhmNode):IFormulaNode
    {
        const contents:string = this.__contents(minusSign, expr);
        mk.debug(area.Formula, `[eval] UnaryExpression_negate: ${contents}`);

        const exprValue:IFormulaNode = expr.eval();
        const result:IFormulaNode = new NegateExpr(exprValue);

        mk.debug(area.Formula, `[eval] UnaryExpression_negate: ${contents} => ${result}`);
        return result;
    }

    UnionExpression_calcColumn(lhs:OhmNode, _bar:OhmNode, rhs:OhmNode):IFormulaNode
    {
        const contents = this.__contents(lhs, _bar, rhs);
        mk.debug(area.Formula, `[eval] UnionExpression_calcColumn: ${contents}`);

        return new UnionReference(lhs.eval(), rhs.eval());
    }

    UnionExpression_union(lhs:OhmNode, _bar:OhmNode, rhs:OhmNode):IFormulaNode
    {
        const contents = this.__contents(lhs, _bar, rhs);
        mk.debug(area.Formula, `[eval] UnionExpression_union: ${contents}`);

        return new UnionReference(lhs.eval(), rhs.eval());
    }

    CellListExpression_list(lhs:OhmNode, guardList:OhmNode, op:OhmNode, rhs:OhmNode):IFormulaNode
    {
        const contents = this.__contents(lhs, op, rhs);
        mk.debug(area.Formula, `[eval] CellListExpression_list: ${contents}`);

        return new ListReference(lhs.eval(), rhs.eval());
    }

    CellIntersectionExpression_intersection(lhs:OhmNode, op:OhmNode, rhs:OhmNode):IFormulaNode
    {
        const contents = this.__contents(lhs, op, rhs);
        mk.debug(area.Formula, `[eval] CellIntersectionExpression_intersection: ${contents}`);

        return new IntersectionReference(lhs.eval(), rhs.eval());
    }

    CellRangeExpression_range(lhs:OhmNode, op:OhmNode, rhs:OhmNode):IFormulaNode
    {
        const contents = this.__contents(lhs, op, rhs);
        mk.debug(area.Formula, `[eval] CellRangeExpression_range: ${contents}`);

        return new RangeReference(lhs.eval(), rhs.eval());
    }

    CallExpression(expr:OhmNode):IFormulaNode
    {
        const contents = this.__contents(expr);
        mk.debug(area.Formula, `[eval] CallExpression: ${contents}`);

        return expr.eval();
    }

    InvokeExpression(functionName:OhmNode, functionArguments:OhmNode):IFormulaNode
    {
        const contents:string = this.__contents(functionName, functionArguments);
        mk.debug(area.Formula, `[eval] InvokeExpression: ${contents}`);

        const functionFullName:AString = functionName.eval();
        const argValues:Array<IFormulaNode> = functionArguments.eval();

        let result = new InvokeFunctionExpr(functionFullName, argValues);

        mk.debug(area.Formula, `[eval] InvokeExpression: ${contents} => ${result}`);
        return result;
    }

    Arguments(_1:OhmNode, argumentsList:any, _2:OhmNode):Array<IFormulaNode>
    {
        const contents:string = this.__contents(_1, argumentsList, _2);
        mk.debug(area.Formula, `[eval] Arguments: ${contents}`);

        if( contents == '()' )
        {
            const emptyArray:Array<IFormulaNode> = [];
            return emptyArray;
        }

        return argumentsList.eval();
    }

    ArgumentExpression(expr:OhmNode):IFormulaNode
    {
        const contents:string = this.__contents(expr);
        mk.debug(area.Formula, `[eval] ArgumentExpression: ${contents}`);

        const argument:IFormulaNode[] = expr.eval();

        // argument is optional
        if( argument.length == 1 )
            return argument[0];

        return new ValueLiteral(null);
    }

    NonemptyListOf(element:OhmNode, separator:OhmNode, restElements:OhmNode):Array<IFormulaNode>
    {
        const contents:string = this.__contents(element, restElements);
        mk.debug(area.Formula, `[eval] NonemptyListOf: ${contents}`);

        let values:Array<IFormulaNode> = [];

        if( element )
            values.push(element.eval());

        if( restElements )
            values = values.concat(restElements.eval());

        return values;
    }

    EmptyListOf():Array<IFormulaNode>
    {
        mk.debug(area.Formula, `[eval] EmptyListOf`);

        let values:Array<IFormulaNode> = [];
        return values;
    }

    PrimaryExpression_cell(expr:OhmNode):IFormulaNode
    {
        const contents = this.__contents(expr);
        mk.debug(area.Formula, `[eval] PrimaryExpression_cell: ${contents}`);

        return expr.eval();
    }

    PrimaryExpression_constant(expr:OhmNode):IFormulaNode
    {
        const contents = this.__contents(expr);
        mk.debug(area.Formula, `[eval] PrimaryExpression_constant: ${contents}`);

        return expr.eval();
    }

    PrimaryExpression_paren(expr:OhmNode):IFormulaNode
    {
        const contents = this.__contents(expr);
        mk.debug(area.Formula, `[eval] PrimaryExpression_paren: ${contents}`);

        return expr.eval();
    }

    PrimaryExpression_array(expr:OhmNode):IFormulaNode
    {
        const contents = this.__contents(expr);
        mk.debug(area.Formula, `[eval] PrimaryExpression_array: ${contents}`);

        return expr.eval();
    }

    PrimaryExpression_frameColumn(expr:OhmNode):IFormulaNode
    {
        const contents = this.__contents(expr);
        mk.debug(area.Formula, `[eval] PrimaryExpression_frameColumn: ${contents}`);

        return expr.eval();
    }

    FrameColumnReferenceExpression(expr:OhmNode):IFormulaNode
    {
        const contents = this.__contents(expr);
        mk.debug(area.Formula, `[eval] FrameColumnReferenceExpression: ${contents}`);

        return expr.eval();
    }

    FrameCalculatedColumnExpression(columnName:OhmNode, _eq:OhmNode, expr:OhmNode):IFormulaNode
    {
        const contents = this.__contents(columnName, _eq, expr);
        mk.debug(area.Formula, `[eval] FrameCalculatedColumnExpression: ${contents}`);

        const frameColumnName = columnName.eval();
        const calculatedExpr = expr.eval();

        const frameCalculatedColumnExpr = new FrameCalculatedColumnExpr(frameColumnName, calculatedExpr);

        mk.debug(area.Formula, `[eval] FrameCalculatedColumnExpression: ${contents} => ${frameCalculatedColumnExpr}`);

        return frameCalculatedColumnExpr;
    }

    ArrayExpression(_1:OhmNode, expr:OhmNode, _2:OhmNode):IFormulaNode
    {
        const contents = this.__contents(_1, expr, _2);
        mk.debug(area.Formula, `[eval] ArrayExpression: ${contents}`);

        const exprEval = expr.eval();
        const arrayExpr = new ArrayExpr(exprEval);

        mk.debug(area.Formula, `[eval] ArrayExpression: ${contents} => ${arrayExpr}`);

        return arrayExpr;
    }

    ArrayRowList(expr:OhmNode):IFormulaNode
    {
        const contents = this.__contents(expr);
        mk.debug(area.Formula, `[eval] ArrayRowList: ${contents}`);

        return expr.eval();
    }

    ArrayRow(expr:OhmNode):IFormulaNode
    {
        const contents = this.__contents(expr);
        mk.debug(area.Formula, `[eval] ArrayRow: ${contents}`);

        return expr.eval();
    }

    ParenExpression(_1:OhmNode, expr:OhmNode, _2:OhmNode):IFormulaNode
    {
        const contents = this.__contents(_1, expr, _2);
        mk.debug(area.Formula, `[eval] ParenExpressions: ${contents}`);

        const exprEval = expr.eval();
        const parenExpr = new ParenExpr(exprEval);

        mk.debug(area.Formula, `[eval] ParenExpressions: ${contents} => ${parenExpr}`);

        return parenExpr;
    }

    ConstantExpression_bool(expr:OhmNode):IFormulaNode
    {
        const contents = this.__contents(expr);
        mk.debug(area.Formula, `[eval] ConstantExpression_bool: ${contents}`);

        const value:IValue = expr.eval();
        const result = new ValueLiteral(value);

        mk.debug(area.Formula, `[eval] ConstantExpression_bool: ${contents} => ${result}`);
        return result;
    }

    ConstantExpression_error(expr:OhmNode):IFormulaNode
    {
        const contents = this.__contents(expr);
        mk.debug(area.Formula, `[eval] ConstantExpression_error: ${contents}`);

        const value = AError.fromString(contents);
        let result = new ValueLiteral(value);

        mk.debug(area.Formula, `[eval] ConstantExpression_error: ${contents} => ${result}`);
        return result;
    }

    ConstantExpression_identifier(expr:OhmNode):IFormulaNode
    {
        const contents = this.__contents(expr);
        mk.debug(area.Formula, `[eval] ConstantExpression_identifier: ${contents}`);

        const value:AString = expr.eval();
        const result = new IdentifierLiteral(value);

        mk.debug(area.Formula, `[eval] ConstantExpression_identifier: ${contents} => ${result}`);
        return result;
    }

    ConstantExpression_number(expr:OhmNode):IFormulaNode
    {
        const contents = this.__contents(expr);
        mk.debug(area.Formula, `[eval] ConstantExpression_number: ${contents}`);

        const value:IValue = expr.eval();
        const result = new ValueLiteral(value);

        mk.debug(area.Formula, `[eval] ConstantExpression_number: ${contents} => ${result}`);
        return result;
    }

    ConstantExpression_string(expr:OhmNode):IFormulaNode
    {
        const contents = this.__contents(expr);
        mk.debug(area.Formula, `[eval] ConstantExpression_string: ${contents}`);

        const value:IValue = expr.eval();
        const result = new ValueLiteral(value);

        mk.debug(area.Formula, `[eval] ConstantExpression_string: ${contents} => ${result}`);
        return result;
    }

    boolean_true(trueString:OhmNode):ABoolean
    {
        const contents:string = this.__contents(trueString);
        mk.debug(area.Formula, `[eval] boolean_true: ${contents}`);

        const result = ABoolean.true();

        mk.debug(area.Formula, `[eval] boolean_true: ${contents} => ${result}`);
        return result;
    }

    boolean_false(trueString:OhmNode):ABoolean
    {
        const contents:string = this.__contents(trueString);
        mk.debug(area.Formula, `[eval] boolean_false: ${contents}`);

        const result = ABoolean.false();

        mk.debug(area.Formula, `[eval] boolean_false: ${contents} => ${result}`);
        return result;
    }

    identifier(identifierLiteral:OhmNode):AString
    {
        const contents:string = this.__contents(identifierLiteral);
        mk.debug(area.Formula, `[eval] identifier: ${contents}`);

        const result = AString.fromString(`${contents}`);

        mk.debug(area.Formula, `[eval] identifier: ${contents} => ${result}`);
        return result;
    }

    stringCharacter_normal(_1:OhmNode):string
    {
        return this.__contents(_1);
    }

    stringCharacter_quote(_1:OhmNode, _2:OhmNode):string
    {
        return '"';
    }

    stringCharacter_escaped(_1:OhmNode, _2:OhmNode):string
    {
        const letter = this.__contents(_2);
        switch( letter )
        {
            case 'r':
                return '\r';
            case 'n':
                return '\n';
            case 't':
                return '\t';
            default:
                return letter;
        }
    }

    stringCharacter_unicode(_1:OhmNode, d1:OhmNode, d2:OhmNode, d3:OhmNode, d4:OhmNode):string
    {
        const charCodeString = [d1, d2, d3, d4].map(d => this.__contents(d)).join("");
        const charCode = parseInt(charCodeString, 16);
        return String.fromCharCode(charCode);
    }

    stringCharacters(charactersNode:OhmNode):string
    {
        let result = '';
        for( let characterNode of charactersNode.children )
            result += characterNode.eval();

        return result;
    }

    stringLiteral(_1:OhmNode, charactersNode:OhmNode, _2:OhmNode):AString
    {
        const contents:string = this.__contents(_1, charactersNode, _2);
        mk.debug(area.Formula, `[eval] stringLiteral: ${contents}`);

        const result = AString.fromString(charactersNode.eval());

        mk.debug(area.Formula, `[eval] stringLiteral: ${contents} => ${result}`);
        return result;
    }

    numberLiteral(decimalLiteral:OhmNode, exponent_part:OhmNode):IValue
    {
        const contents:string = this.__contents(decimalLiteral, exponent_part);
        mk.debug(area.Formula, `[eval] numberLiteral: ${contents}`);

        return decimalLiteral.eval();
    }

    decimalLiteral(decimalLiteral:OhmNode):ANumber
    {
        const contents:string = this.__contents(decimalLiteral);
        mk.debug(area.Formula, `[eval] decimalLiteral: ${contents}`);

        const number:number = parseFloat(contents);

        return ANumber.fromNumber(number);
    }

    integerLiteral(integerLiteral:OhmNode):AInteger
    {
        const contents:string = this.__contents(integerLiteral);
        mk.debug(area.Formula, `[eval] integerLiteral: ${contents}`);

        const wholeNumber = parseInt(contents);
        return AInteger.fromNumber(wholeNumber);
    }

    CellReference(expr:OhmNode):IFormulaNode
    {
        const contents = this.__contents(expr);
        mk.debug(area.Formula, `[eval] CellReference: ${contents}`);

        return expr.eval();
    }

    CellReference_external(expr:OhmNode):IFormulaNode
    {
        const contents = this.__contents(expr);
        mk.debug(area.Formula, `[eval] CellReference_external: ${contents}`);

        return expr.eval();
    }

    CellReference_internal(cellRefLiteral:OhmNode):IFormulaNode
    {
        const value:CellReference | RangeReference = cellRefLiteral.eval();

        if( value instanceof CellReference )
        {
            const cellRef:CellReference = value as CellReference;

            mk.debug(area.Formula, `[eval] CellReference_internal: col=${cellRef.colIndex} row=${cellRef.rowIndex}`);

            if( cellRef.colIndex != null && cellRef.rowIndex != null )
                return cellRef;
            else
                new ValueLiteral(EvalErrors.CellRef);
        }

        if( value instanceof RangeReference )
        {
            const rangeRef:RangeReference = value as RangeReference;

            mk.debug(area.Formula, `[eval] CellReference_internal: from=${rangeRef.from} to=${rangeRef.to}`);

            if( rangeRef.from != null && rangeRef.to != null )
                return rangeRef;
            else
                new ValueLiteral(EvalErrors.CellRef);
        }
    }

    externalCellReference(sheetNameNode:OhmNode, _excl:OhmNode, internalCellRefNode:OhmNode):IFormulaNode
    {
        const contents:string = this.__contents(sheetNameNode, _excl, internalCellRefNode);
        mk.debug(area.Formula, `[eval] externalCellReference: ${contents}`);

        const sheetName:AString = sheetNameNode.eval();
        const cellRef:CellReference | RangeReference = internalCellRefNode.eval();

        const sheetNameForSearch = sheetName.value.toUpperCase();
        const sheetId:SheetId = this._context.findSheetIdByName(this._context.workbookId, sheetNameForSearch);

        mk.debug(area.Formula, `[eval] externalCellReference: name="${sheetName}" cellRef=${cellRef}`);

        if( !sheetId )
            return new ValueLiteral(EvalErrors.CellRef);
        else if( cellRef instanceof CellReference )
        {
            (cellRef as CellReference).updateToExternalRef(sheetId);
            return cellRef;
        }
        else if( cellRef instanceof RangeReference )
        {
            (cellRef as RangeReference).updateToExternalRef(sheetId);
            return cellRef;
        }
        else
            new ValueLiteral(EvalErrors.CellRef);
    }

    frameColumnReference(_at:OhmNode, identifier:OhmNode):IFormulaNode
    {
        const contents:string = this.__contents(_at, identifier);
        mk.debug(area.Formula, `[eval] frameColumnReference: ${contents}`);

        const columnName:AString = identifier.eval();
        return new FrameColumnReference(columnName);
    }

    rcInternalCellReference_area(lhs:OhmNode, op:OhmNode, rhs:OhmNode):RangeReference
    {
        const contents = this.__contents(lhs, op, rhs);

        const lCellRef:CellReference = lhs.eval();
        const rCellRef:CellReference = rhs.eval();

        const result:RangeReference = new RangeReference(lCellRef, rCellRef);
        mk.debug(area.Formula, `[eval] rcInternalCellReference_area: ${contents} => ${result.toString()}`);
        return result;
    }

    rcInternalCellReference_cell(cellRefNode:OhmNode):CellReference
    {
        const contents = this.__contents(cellRefNode);
        const cellRef:CellReference = cellRefNode.eval();

        const result = cellRef;
        mk.debug(area.Formula, `[eval] rcInternalCellReference_cell: ${contents} => ${result.toString()}`);
        return result;
    }

    CellReference_error(expr:OhmNode):IFormulaNode
    {
        const contents = this.__contents(expr);
        const result = new ValueLiteral(AError.fromString(contents));

        mk.debug(area.Formula, `[eval] CellReference_error: ${contents} => ${result}`);
        return result;
    }

    singleSheetName(nameNode:OhmNode):IValue
    {
        const contents:string = this.__contents(nameNode);
        mk.debug(area.Formula, `[eval] singleSheetName: ${contents}`);

        let result = nameNode.eval();

        mk.debug(area.Formula, `[eval] singleSheetName: ${contents} => ${result}`);
        return result;
    }

    sheetSpecialName(_1:OhmNode, nameLiteral:OhmNode, _2:OhmNode):AString
    {
        const contents:string = this.__contents(_1, nameLiteral, _2);
        mk.debug(area.Formula, `[eval] sheetSpecialName: ${contents}`);

        const stringRaw:string = this.__contents(nameLiteral);

        const result = AString.fromString(stringRaw.replace('\'\'', '\''));

        mk.debug(area.Formula, `[eval] sheetSpecialName: ${contents} => ${result}`);
        return result;
    }

    a1InternalCellReference(expr:OhmNode):RangeReference | CellReference
    {
        const contents:string = this.__contents(expr);

        mk.debug(area.Formula, `[eval] a1InternalCellReference: contents=${contents}`);

        const result = expr.eval();
        mk.debug(area.Formula, `[eval] a1InternalCellReference: ${contents} => ${result.toString()}`);
        return result;
    }

    a1InternalCellReference_area(lhs:OhmNode, _1:OhmNode, rhs:OhmNode):RangeReference
    {
        const contents:string = this.__contents(lhs, _1, rhs);

        const lCellRef:CellReference = lhs.eval();
        const rCellRef:CellReference = rhs.eval();

        mk.debug(area.Formula, `[eval] a1InternalCellReference_area: contents=${contents}`);

        const result:RangeReference = new RangeReference(lCellRef, rCellRef);
        mk.debug(area.Formula, `[eval] a1InternalCellReference_area: ${contents} => ${result.toString()}`);
        return result;
    }

    a1InternalCellReference_vLVector(lhs:OhmNode, _1:OhmNode, rhs:OhmNode):RangeReference
    {
        const contents:string = this.__contents(lhs, _1, rhs);

        const lCellRef:CellReference = lhs.eval();
        const rCellRef:CellReference = rhs.eval();

        mk.debug(area.Formula, `[eval] a1InternalCellReference_vLVector: contents=${contents}`);

        const result:RangeReference = new RangeReference(lCellRef, rCellRef);
        mk.debug(area.Formula, `[eval] a1InternalCellReference_vLVector: ${contents} => ${result.toString()}`);
        return result;
    }

    a1InternalCellReference_vRVector(lhs:OhmNode, _1:OhmNode, rhs:OhmNode):RangeReference
    {
        const contents:string = this.__contents(lhs, _1, rhs);

        const lCellRef:CellReference = lhs.eval();
        const rCellRef:CellReference = rhs.eval();

        mk.debug(area.Formula, `[eval] a1InternalCellReference_vRVector: contents=${contents}`);

        const result:RangeReference = new RangeReference(lCellRef, rCellRef);
        mk.debug(area.Formula, `[eval] a1InternalCellReference_vRVector: ${contents} => ${result.toString()}`);
        return result;
    }

    a1InternalCellReference_hLVector(lhs:OhmNode, _1:OhmNode, rhs:OhmNode):RangeReference
    {
        const contents:string = this.__contents(lhs, _1, rhs);

        const lCellRef:CellReference = lhs.eval();
        const rCellRef:CellReference = rhs.eval();

        mk.debug(area.Formula, `[eval] a1InternalCellReference_hLVector: contents=${contents}`);

        const result:RangeReference = new RangeReference(lCellRef, rCellRef);
        mk.debug(area.Formula, `[eval] a1InternalCellReference_hLVector: ${contents} => ${result.toString()}`);
        return result;
    }

    a1InternalCellReference_hRVvector(lhs:OhmNode, _1:OhmNode, rhs:OhmNode):RangeReference
    {
        const contents:string = this.__contents(lhs, _1, rhs);

        const lCellRef:CellReference = lhs.eval();
        const rCellRef:CellReference = rhs.eval();

        mk.debug(area.Formula, `[eval] a1InternalCellReference_hRVvector: contents=${contents}`);

        const result:RangeReference = new RangeReference(lCellRef, rCellRef);
        mk.debug(area.Formula, `[eval] a1InternalCellReference_hRVvector: ${contents} => ${result.toString()}`);
        return result;
    }

    a1InternalCellReference_hVector(lhs:OhmNode, _1:OhmNode, rhs:OhmNode):RangeReference
    {
        const contents:string = this.__contents(lhs, _1, rhs);

        const lCellRef:CellReference = lhs.eval();
        const rCellRef:CellReference = rhs.eval();

        mk.debug(area.Formula, `[eval] a1InternalCellReference_hVector: contents=${contents}`);

        const result:RangeReference = new RangeReference(lCellRef, rCellRef);
        mk.debug(area.Formula, `[eval] a1InternalCellReference_hVector: ${contents} => ${result.toString()}`);
        return result;
    }

    a1InternalCellReference_vVector(lhs:OhmNode, _1:OhmNode, rhs:OhmNode):RangeReference
    {
        const contents:string = this.__contents(lhs, _1, rhs);

        const lCellRef:CellReference = lhs.eval();
        const rCellRef:CellReference = rhs.eval();

        mk.debug(area.Formula, `[eval] a1InternalCellReference_vVector: contents=${contents}`);

        const result:RangeReference = new RangeReference(lCellRef, rCellRef);
        mk.debug(area.Formula, `[eval] a1InternalCellReference_vVector: ${contents} => ${result.toString()}`);
        return result;
    }

    a1InternalCellReference_cell(cellRef:OhmNode):CellReference
    {
        const contents:string = this.__contents(cellRef);

        mk.debug(area.Formula, `[eval] a1InternalCellReference_cell: contents=${contents}`);

        const result:CellReference = cellRef.eval();
        mk.debug(area.Formula, `[eval] a1InternalCellReference_cell: ${contents} => ${result.toString()}`);
        return result;
    }

    a1CellReference(column:OhmNode, row:OhmNode):CellReference
    {
        const contents:string = this.__contents(column, row);

        const columnRef:CellReference = column.eval();
        const rowRef:CellReference = row.eval();

        mk.debug(area.Formula, `[eval] a1CellReference: contents=${contents}`);

        const cellReference = new CellReference(this._context.sheetId, columnRef.colIndex, rowRef.rowIndex);

        mk.debug(area.Formula, `[eval] a1CellReference: contents=${contents} => ${cellReference.toString()}`);

        return cellReference;
    }

    a1Column_fixed(_1:OhmNode, expr:OhmNode):CellReference
    {
        const contents:string = this.__contents(_1, expr);
        const exprContents:string = this.__contents(expr);

        mk.debug(area.Formula, `[eval] a1Column_fixed: ${contents}`);

        const colIndex:number = MeshLocationUtils.getColumnIndexByLetter(exprContents);
        const result = new CellReference(this._context.sheetId, colIndex, -1);

        mk.debug(area.Formula, `[eval] a1Column_fixed: ${contents} => ${result}`);
        return result;
    }

    a1Column_nonFixed(expr:OhmNode):CellReference
    {
        const contents:string = this.__contents(expr);

        mk.debug(area.Formula, `[eval] a1Column_nonFixed: ${contents}`);

        const colIndex:number = MeshLocationUtils.getColumnIndexByLetter(contents);
        const result = new CellReference(this._context.sheetId, colIndex, -1);

        mk.debug(area.Formula, `[eval] a1Column_nonFixed: ${contents} => ${result}`);
        return result;
    }

    a1Row_fixed(_1:OhmNode, expr:OhmNode):CellReference
    {
        const contents:string = this.__contents(_1, expr);
        const exprContents:string = this.__contents(expr);

        mk.debug(area.Formula, `[eval] a1Row_fixed: ${contents}`);

        const rowNumber = parseInt(exprContents);
        const result = new CellReference(this._context.sheetId, -1, rowNumber - 1);

        mk.debug(area.Formula, `[eval] a1Row_fixed: ${contents} => ${result}`);
        return result;
    }

    a1Row_nonFixed(expr:OhmNode):CellReference
    {
        const contents:string = this.__contents(expr);

        mk.debug(area.Formula, `[eval] a1Row_nonFixed: ${contents}`);

        const rowNumber = parseInt(contents);
        const result = new CellReference(this._context.sheetId, -1, rowNumber - 1);

        mk.debug(area.Formula, `[eval] a1Row_nonFixed: ${contents} => ${result}`);
        return result;
    }

    rcCellReference_row_col(rowPart:OhmNode, colPart:OhmNode):CellReference
    {
        const contents:string = this.__contents(rowPart, colPart);

        mk.debug(area.Formula, `[eval] rcCellReference_row_col: ${contents}`);

        const rowResult:AInteger = rowPart.eval();

        const colResultArray:AInteger[] = colPart.eval();

        if( colResultArray.length > 0 )
        {
            const colResult:AInteger = colResultArray[0];

            const result = new CellReference(this._context.sheetId, colResult.value, rowResult.value);
            mk.debug(area.Formula, `[eval] rcCellReference_row_col: ${contents} => ${result}`);

            return result;
        }

        const result = new CellReference(this._context.sheetId, -1, rowResult.value);
        mk.debug(area.Formula, `[eval] rcCellReference_row_col: ${contents} => ${result}`);

        return result;
    }

    rcCellReference_col_row(colPart:OhmNode, rowPart:OhmNode):CellReference
    {
        const contents:string = this.__contents(colPart, rowPart);

        mk.debug(area.Formula, `[eval] rcCellReference_col_row: ${contents}`);

        const colResult:AInteger = colPart.eval();

        const rowResultArray:AInteger[] = rowPart.eval();

        if( rowResultArray.length > 0 )
        {
            const rowResult:AInteger = rowResultArray[0];

            const result = new CellReference(this._context.sheetId, colResult.value, rowResult.value);
            mk.debug(area.Formula, `[eval] rcCellReference_col_row: ${contents} => ${result}`);
        }

        const result = new CellReference(this._context.sheetId, colResult.value, -1);
        mk.debug(area.Formula, `[eval] rcCellReference_col_row: ${contents} => ${result}`);

        return result;
    }

    rowReference(expr:OhmNode):AInteger
    {
        const contents:string = this.__contents(expr);
        mk.debug(area.Formula, `[eval] rowReference: ${contents}`);

        const result:AInteger = expr.eval();

        mk.debug(area.Formula, `[eval] rowReference: ${contents} => ${result}`);
        return result;
    }

    rowReference_current(colSymbol:OhmNode):AInteger
    {
        const contents:string = this.__contents(colSymbol);
        mk.debug(area.Formula, `[eval] rowReference_current: ${contents}`);

        let index = this._context.rowIndex;
        let result:AInteger = AInteger.fromNumber(index);

        mk.debug(area.Formula, `[eval] rowReference_current: ${contents} => ${result}`);
        return result;
    }

    rowReference_absolute(colSymbol:OhmNode, valuePart:OhmNode):AInteger
    {
        const contents:string = this.__contents(colSymbol, valuePart);
        mk.debug(area.Formula, `[eval] rowReference_absolute: ${contents}`);

        const rowNumber:AInteger = valuePart.eval();
        let result = AInteger.fromNumber(rowNumber.value - 1);

        mk.debug(area.Formula, `[eval] rowReference_absolute: ${contents} => ${result}`);
        return result;
    }

    rowReference_relative(colSymbol:OhmNode, relativeValue:OhmNode):AInteger
    {
        const contents:string = this.__contents(colSymbol, relativeValue);
        mk.debug(area.Formula, `[eval] rowReference_relative: ${contents}`);

        let relativeValueResult:AInteger = relativeValue.eval();

        let rowIndex = this._context.rowIndex;
        let resultValue = rowIndex + relativeValueResult.value;

        let result:AInteger = AInteger.fromNumber(resultValue);

        mk.debug(area.Formula, `[eval] rowReference_relative: ${contents}, row=${rowIndex} => ${result}`);
        return result;
    }

    columnReference(expr:OhmNode):AInteger
    {
        const contents:string = this.__contents(expr);
        mk.debug(area.Formula, `[eval] columnReference: ${contents}`);

        const result:AInteger = expr.eval();

        mk.debug(area.Formula, `[eval] columnReference: ${contents} => ${result}`);
        return result;
    }

    columnReference_current(colSymbol:OhmNode):AInteger
    {
        const contents:string = this.__contents(colSymbol);
        mk.debug(area.Formula, `[eval] columnReference_current: ${contents}`);

        let index = this._context.colIndex;
        let result:AInteger = AInteger.fromNumber(index);

        mk.debug(area.Formula, `[eval] columnReference_current: ${contents} => ${result}`);
        return result;
    }

    columnReference_absolute(colSymbol:OhmNode, valuePart:OhmNode):AInteger
    {
        const contents:string = this.__contents(colSymbol, valuePart);
        mk.debug(area.Formula, `[eval] columnReference_absolute: ${contents}`);

        const colNumber:AInteger = valuePart.eval();

        let result:AInteger = AInteger.fromNumber(colNumber.value - 1);

        mk.debug(area.Formula, `[eval] columnReference_absolute: ${contents} => ${result}`);
        return result;
    }

    columnReference_relative(colSymbol:OhmNode, relativeValue:OhmNode):AInteger
    {
        const contents:string = this.__contents(colSymbol, relativeValue);
        mk.debug(area.Formula, `[eval] columnReference_relative: ${contents}`);

        let relativeValueResult:AInteger = relativeValue.eval();

        let colIndex = this._context.colIndex;
        let resultValue = colIndex + relativeValueResult.value;

        let result:AInteger = AInteger.fromNumber(resultValue);

        mk.debug(area.Formula, `[eval] columnReference_relative: ${contents}, col=${colIndex} => ${result}`);
        return result;
    }

    absoluteValue(valuePart:OhmNode):AInteger
    {
        const contents:string = this.__contents(valuePart);
        mk.debug(area.Formula, `[eval] absoluteValue: ${contents}`);

        const wholeNumber = parseInt(contents);
        let result = AInteger.fromNumber(wholeNumber);

        mk.debug(area.Formula, `[eval] absoluteValue: ${contents} => ${result}`);
        return result;
    }

    relativeValue(_1:OhmNode, minus:OhmNode, valuePart:OhmNode, _2:OhmNode):AInteger
    {
        const contents:string = this.__contents(_1, minus, valuePart, _2);
        mk.debug(area.Formula, `[eval] relativeValue: ${contents}`);

        const numberContent:string = this.__contents(valuePart);
        let wholeNumber = parseInt(numberContent);

        if( minus.source.startIdx != minus.source.endIdx )
            wholeNumber = wholeNumber * -1;

        const result = AInteger.fromNumber(wholeNumber);

        mk.debug(area.Formula, `[eval] relativeValue: ${contents} => ${result}`);
        return result;
    }

    _terminal()
    {
        // do nothing
    }

    __evalComparisonExpression(lhs:OhmNode, rhs:OhmNode, opType:CompareOpType):IFormulaNode
    {
        const lhsContents:string = this.__contents(lhs);
        const rhsContents:string = this.__contents(rhs);

        const result = new ComparisonExpr(lhs.eval(), rhs.eval(), opType);

        mk.debug(area.Formula, `[eval] __evalComparisonExpression(): ${lhsContents}, ${rhsContents}, ${opType} => ${result}`);
        return result;
    }

    __evalBinaryOperands(lhs:OhmNode, rhs:OhmNode, opType:BinaryOpType):IFormulaNode
    {
        const lhsContents:string = this.__contents(lhs);
        const rhsContents:string = this.__contents(rhs);

        const result = new BinaryExpr(lhs.eval(), rhs.eval(), opType);

        mk.debug(area.Formula, `[eval] __evalBinaryOperands(): ${lhsContents}, ${rhsContents}, ${opType} => ${result}`);
        return result;
    }
}
